import { useReduxState } from 'hooks';
import { Button } from 'components/common';
import { Form, Text } from 'components/common/form';
import {
  EditorBlockConverter,
  EditorBlockCustomControls
} from 'components/editor/blocks';

import type { Block, BlockList, Course } from 'shared-types';

interface Props {
  blockId: Block['id'];
  data: BlockList;
  isListStartVisible: boolean;
  isOrderedList: boolean;
  languageVersion: Course['language'];
  listStartValue: number;
  onHighlightList?: (bool: boolean) => void;
  onListStartChange: (value: number) => void;
  onListStartClick: () => void;
  onOlClick: () => void;
  onUlClick: () => void;
  onIndent: () => void;
  onOutdent: () => void;
}

const EditorBlockListControls = ({
  blockId,
  data,
  isListStartVisible,
  isOrderedList,
  languageVersion,
  listStartValue,
  onHighlightList,
  onListStartChange,
  onListStartClick,
  onOlClick,
  onUlClick,
  onIndent,
  onOutdent
}: Props) => {
  const { messages } = useReduxState();

  const handleHighlight = onHighlightList
    ? {
        onMouseEnter: () => onHighlightList(true),
        onMouseLeave: () => onHighlightList(false)
      }
    : {};

  return (
    <EditorBlockCustomControls>
      <Button
        category="light"
        data-tooltip={messages.editorBlockListIndent}
        icon="indent"
        onClick={onIndent}
        size="small"
        style={{ marginRight: 5 }}
        {...handleHighlight}
      />
      <Button
        category="light"
        data-tooltip={messages.editorBlockListOutdent}
        icon="outdent"
        onClick={onOutdent}
        size="small"
        style={{ marginRight: 10 }}
        {...handleHighlight}
      />
      <Button
        category="light"
        data-tooltip={messages.editorBlockListUnorderedList}
        icon="list-ul"
        onClick={onUlClick}
        size="small"
        style={{ marginRight: 5 }}
        {...handleHighlight}
      />
      <Button
        category="light"
        data-tooltip={messages.editorBlockListOrderedList}
        icon="list-ol"
        onClick={onOlClick}
        size="small"
        style={{ marginRight: 10 }}
        {...handleHighlight}
      />

      {isOrderedList ? (
        <>
          <Button
            category="light"
            data-tooltip={messages.editorBlockListSetStart}
            icon="sort-numeric-down"
            onClick={onListStartClick}
            size="small"
            style={{ marginRight: 10 }}
            {...handleHighlight}
          />

          {isListStartVisible ? (
            <div style={{ display: 'inline-block' }}>
              <Form initialValues={{ listStartValue }}>
                <Text
                  field="listStartValue"
                  id={`list-start-value-${blockId}`}
                  inputStyle={{
                    maxWidth: 60,
                    padding: '3px 7px 4px',
                    position: 'relative',
                    top: 1
                  }}
                  min={1}
                  onChange={onListStartChange}
                  style={{ marginRight: 10 }}
                  type="number"
                />
              </Form>
            </div>
          ) : null}
        </>
      ) : null}

      <EditorBlockConverter
        blockId={blockId}
        blockType="list"
        data={data}
        languageVersion={languageVersion}
      />
    </EditorBlockCustomControls>
  );
};

export default EditorBlockListControls;
